import { useParams } from "react-router-dom";

import { FilterIcon, TrashIcon } from "@/assets/icons";
import ButtonMenu, { ButtonMenuItem } from "@/components/ui/buttonMenu";
import { COUNTRY_NAME } from "@/constants/dataset";
import { useDownloadDataset } from "@/hooks/dataset-managment/useDownloadDataset";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { CountryCode, SpatialAggregationType } from "@/types/common";
import { DatasetMetadata, ExportDatasetFileType } from "@/types/dataset";
import { EOutputAnalysisType } from "@/types/spatial";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import AggregationBadge from "../AggregationBadge";

export interface FilterSectionProps {
  metadata: DatasetMetadata | undefined;
  isLoading: boolean;
  isDatasetPublished: boolean;
  handleAddMoreDataset: () => void;
  handleReplaceDataset: () => void;
  handleAddNewRowDataset: () => void;
  setshowDataSettingPopup: (show: boolean) => void;
  handleDeleteDataset: () => void;
  handleDeleteRows?: (id?: string) => void;
  deleteRowCount: number;
  resetDeleteRow: () => void;
}

export default function DatasetConfigurationSection({
  metadata,
  isLoading,
  isDatasetPublished,
  handleDeleteDataset,
  handleAddMoreDataset,
  handleReplaceDataset,
  handleAddNewRowDataset,
  setshowDataSettingPopup,
  handleDeleteRows,
  deleteRowCount,
  resetDeleteRow
}: FilterSectionProps) {
  const { id } = useParams();
  const { isRowDatasetManagementEnabled } = useAppFeatures();
  const userPermissions = useGetUserPermission();
  const isEditDataGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.EDIT];
  const isCreateDataGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.CREATE];
  const isDeleteDataGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.DELETE];

  const isDataSettingsDisabled = isLoading || isDatasetPublished;

  const isShowEditDataButton =
    isEditDataGranted || isCreateDataGranted || isDeleteDataGranted;

  const { handleDownloadDataset } = useDownloadDataset();
  const getEditDataMenu = (): ButtonMenuItem[] => {
    const output = [];
    // If Dataset have metadata spatial_aggregation_type, disable Add New Row feature until updated to support it
    if (isRowDatasetManagementEnabled && isCreateDataGranted) {
      output.push(
        {
          name: "Action",
          id: "action-header",
          isHeader: true
        },
        {
          name: "Add New Row",
          id: "add-new-row",
          onClick: handleAddNewRowDataset
        },
        {
          name: "Advanced Action",
          id: "advanced-action-header",
          isHeader: true
        }
      );
    }
    if (isCreateDataGranted) {
      output.push({
        name: "Add More Data",
        id: "add-more-data",
        onClick: handleAddMoreDataset
      });
    }
    if (isEditDataGranted) {
      output.push({
        name: "Replace All Data",
        id: "replace-all-data",
        onClick: handleReplaceDataset
      });
    }
    if (isDeleteDataGranted) {
      output.push({
        name: "Delete Dataset",
        id: "delete-dataset",
        onClick: handleDeleteDataset
      });
    }
    return output;
  };

  const getDownloadMenu = (): ButtonMenuItem[] => {
    return [
      {
        name: "CSV",
        onClick: () =>
          handleDownloadDataset(ExportDatasetFileType.CSV, id as string)
      },
      {
        name: "XLSX",
        onClick: () =>
          handleDownloadDataset(ExportDatasetFileType.XLSX, id as string)
      }
    ];
  };

  return (
    <div className="flex gap-4">
      <div className="flex-center gap-2">
        {isEditDataGranted && deleteRowCount === 0 && (
          <Button
            disabled={isDataSettingsDisabled}
            className={twMerge(
              "btn-secondary px-4",
              metadata?.published && "hidden"
            )}
            onClick={() => setshowDataSettingPopup(true)}
          >
            <FilterIcon
              className={twMerge(
                "fill-teal-500",
                isDataSettingsDisabled && "fill-gainsboro"
              )}
              width={16}
            />
            <span className="font-semibold text-sm">Data Settings</span>
          </Button>
        )}
        {!!metadata?.spatial_aggregation_type && deleteRowCount === 0 && (
          <AggregationBadge
            value={
              metadata.spatial_aggregation_type as Exclude<
                SpatialAggregationType,
                EOutputAnalysisType
              >
            }
          />
        )}
        {deleteRowCount > 0 && (
          <p className="pl-4 pt-4 font-semibold text-txtHeading">
            {deleteRowCount} row(s) selected
          </p>
        )}
        {!!metadata?.country_id && deleteRowCount === 0 && (
          <div className="border border-gainsboro bg-whisper rounded-lg flex-center px-2 h-6">
            <span className="font-semibold">
              {COUNTRY_NAME[metadata?.country_id as CountryCode]}
            </span>
          </div>
        )}
      </div>

      <div className="ml-auto flex flex-row items-center gap-2">
        {deleteRowCount > 0 && (
          <Button
            className="btn-secondary px-6"
            onClick={() => {
              resetDeleteRow();
            }}
          >
            <span className="font-semibold text-sm">Cancel</span>
          </Button>
        )}
        {isEditDataGranted && deleteRowCount === 0 && (
          <ButtonMenu
            title="Download"
            btnContainerClassName="w-[200px] !min-w-[100px] btn-secondary"
            menuClassName="top-[42px] w-[120px] left-0 h-fit z-[99]"
            menu={getDownloadMenu()}
            disabled={isLoading}
          />
        )}
        {isShowEditDataButton && deleteRowCount === 0 && (
          <ButtonMenu
            title="Edit Data"
            btnContainerClassName="w-[200px] !min-w-[100px] btn-secondary"
            menuClassName="top-[42px] w-full right-0 h-fit z-[99]"
            menu={getEditDataMenu()}
            disabled={isLoading}
          />
        )}
        {deleteRowCount > 0 && (
          <Button
            className="btn-secondary px-6"
            onClick={() => handleDeleteRows?.()}
          >
            <TrashIcon className="stroke-primary-teal" />
            <span className="font-semibold text-sm">Delete rows</span>
          </Button>
        )}
      </div>
    </div>
  );
}
